import { useEffect, useState } from 'react';
import { getTable, type TableResult } from './api';
import type { RuntimeConfig } from './auth/oauth';
import { getTokens, signOut } from './auth/session';
import { DataTable } from './components/DataTable';
import { TABLES, type Row, type TableName } from './table';

const TAB_NAMES = Object.keys(TABLES) as TableName[];

function tabFromPath(): TableName {
  const seg = window.location.pathname.replace(/^\/+/, '').split('/')[0];
  return (TAB_NAMES as string[]).includes(seg) ? (seg as TableName) : 'projects';
}

function userEmail(): string | undefined {
  const token = getTokens()?.idToken;
  try {
    const payload = JSON.parse(atob(token!.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as { email?: string };
    return payload.email;
  } catch {
    return undefined; // the dev seed's token is not a JWT
  }
}

export function App({ config }: { config: RuntimeConfig }) {
  const [tab, setTab] = useState<TableName>(tabFromPath);
  const [data, setData] = useState<Partial<Record<TableName, TableResult>>>({});
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const onPop = () => setTab(tabFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (data[tab]) return;
    setError(undefined);
    getTable(config, tab)
      .then((result) => setData((d) => ({ ...d, [tab]: result })))
      .catch((err: Error) => setError(err.message));
  }, [tab, config, data]);

  const go = (next: TableName) => {
    window.history.pushState(null, '', `/${next}`);
    setTab(next);
  };
  const result = data[tab];

  return (
    <div className="app">
      <header>
        <h1>Replit audit</h1>
        <nav>
          {TAB_NAMES.map((t) => (
            <button key={t} type="button" className={t === tab ? 'active' : ''} onClick={() => go(t)}>
              {TABLES[t].title}
            </button>
          ))}
        </nav>
        <span className="spacer" />
        <span className="who">{userEmail()}</span>
        <button type="button" onClick={() => void signOut(config).then((r) => r === 'local' && window.location.assign('/signed-out'))}>
          Sign out
        </button>
      </header>
      <main>
        {error && <p className="error" role="alert">{error}</p>}
        {!error && !result && <p className="muted">Loading…</p>}
        {result && (
          <>
            <Summary tab={tab} result={result} />
            <DataTable
              key={tab}
              columns={TABLES[tab].columns}
              rows={result.rows}
              filename={`replit-${tab}-${result.generatedAt.slice(0, 10)}`}
            />
          </>
        )}
      </main>
    </div>
  );
}

function Summary({ tab, result }: { tab: TableName; result: TableResult }) {
  const s = (result.summary ?? {}) as Record<string, unknown>;
  const when = new Date(result.generatedAt).toLocaleString();
  let line = '';
  if (tab === 'projects') {
    line = `${s.projects} projects in ${s.workspaces} workspaces · ${s.withDeployment} deployed · ${s.stalePublic} stale public`;
    const mm = s.countMismatches as unknown[] | undefined;
    if (mm?.length) line += ` · ${mm.length} workspace(s) returned fewer projects than they report`;
  } else if (tab === 'findings') {
    const by = (sev: string) => result.rows.filter((r: Row) => r.severity === sev).length;
    line = `${by('high')} high · ${by('medium')} medium · ${by('low')} low · ${by('info')} info`;
  } else {
    line = `${result.rows.length} people · ${result.rows.filter((r: Row) => r.flags).length} with flags`;
  }
  return <p className="summary">{line} <span className="muted">· run {when}</span></p>;
}
